import { h, Component, createRef } from 'preact';
import cx from 'classnames';
import moment from 'moment-timezone';

import { Formats } from '../../../../constants/api';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { residentialPropTypes, commercialPropTypes, REQUIRES_BUILDING } from '../../../../constants';
import getStore, { appState$ } from '../../store';
import { DELETE_LISING } from '../../reducers/listing/listing.actions';

import { Listing, Location, DivCat, REQUEST_SUCCESS, REQUEST_FAILED, KumitaMultimedia, REQUEST_IN_PROGRESS, RequestState, REQUEST_UNTOUCHED, User } from '../../../../types';
import hopplerLinkState, { hopplerUpdateFormFieldValue } from '../../../utils/forms/hoppler-linkstate';
import { Form, FieldList } from '../../../utils/forms/form';
import * as Validators from '../../../utils/forms/validators';
import { constructFields } from '../../form-fields/listing-editor.fields';
import { PostPropertyLocation } from './post-property-location.component';
import { PostPropertyPhoto } from './post-property-photo.component';
import { MapUtils } from '../../../utils/map-utils';
import { UploadablePhoto } from '../../../../types/uploadable-photo.model';
import { photoUploadService } from '../../../utils/photo-upload.service';

import { ListingEditorPrimaryContactSection } from './editor/primary-contact.component';
import { ListingEditorAlternativeContactSection } from './editor/alternative-contact.component';
import { ListingEditorMultimediaSection } from './editor/multimedia.component';
import { ListingEditorOtherSection } from './editor/other.component';
import { ListingEditorStatusSection } from './editor/status.component';
import { ListingEditorAmenitiesSection } from './editor/amenities.component';
import { ListingEditorEssentialSection } from './editor/essential.component';
import { HopplerDatePicker } from '../common/hoppler-date-picker.component';
import { DatePickerControl } from '../common/date-picker-control.component';

export interface ListingEditorProps {
  currentTab?: string;
  listing?: Listing;
  reasonVisibility?: boolean
  close?: Function;
  setOnSave?: Function;
}

export interface ListingEditorState {
  currentUser?: User;

  isPending?: number;
  isOnHold?: boolean;

  reason?: string;
  reasonInputVisible?: boolean;
  deletePropertyStatus?: any;

  form?: Form;
  locationForm?: Form;
  requiresBuilding?: boolean;
  reasonVisible?: boolean;

  currentDivCat?: DivCat;
  currentLocation?: Location;
  addreNoLabel?: string;
  searchFieldLabel?: string;

  isEditingPhotos?: boolean;
  isReviewingPhotos?: boolean;
  photosForUpload?: UploadablePhoto[];
  multimedia?: KumitaMultimedia[];
  hasUploadedPhotos?: boolean;
  photoUploadState?: RequestState;
}

export class ListingEditor extends Component<ListingEditorProps, ListingEditorState> {

  form$: Subject<Form>;
  private subscriptions: Subscription[] = [];
  mapUtils = new MapUtils();

  onHoldRef = createRef()
  reasonRef = createRef();

  constructor(props: ListingEditorProps) {
    super(props);

    this.setupForm(props);

    props.setOnSave(this.onSave);
    // Are we loading a complete listing from the API (i.e. not in Post Property)
    if (props.listing.address) {
      let location: Location = {
        region: props.listing.region,
        city: props.listing.city,
        area: props.listing.area,
        building: props.listing.building,
        name: props.listing.name,
        label: props.listing.address,
        unitNo: props.listing.unitNo,
        tower: props.listing.tower,
        streetName: props.listing.streetName
      };

      this.setState({
        currentLocation: location,
        isPending: this.props.listing.isPending,
        isOnHold: false
      });
    }

    const currentDivCat: DivCat = {
      division: props.listing.division,
      category: props.listing.category,
      type: props.listing.type,
    };

    let locationLabel = 'Area name';
    let addreNoLabel = 'Lot number';

    const requiresBuilding = REQUIRES_BUILDING.find(member => {
      return member.label === props.listing.type;
    });

    if (requiresBuilding) {
      locationLabel = 'Building name';
      addreNoLabel = 'Unit number';
    } else if (props.listing.type === 'House and Lot' ||
      props.listing.type === 'Townhouse') {
      addreNoLabel = 'House number';
    } else if (props.listing.type === 'Warehouse') {
      addreNoLabel = 'Unit number';
    } else if (props.listing.type === 'Resort' ||
      props.listing.type === 'Building') {
      locationLabel = 'Tower name/number';
      addreNoLabel = 'Street number';
    }

    this.setState({
      currentDivCat: currentDivCat,
      addreNoLabel: addreNoLabel,
      searchFieldLabel: locationLabel,
      requiresBuilding: requiresBuilding ? true : false,
      reasonInputVisible: false,
      deletePropertyStatus: ""
    });

    if (props.listing.multimedia) {
      this.setState({
        multimedia: props.listing.multimedia,
        hasUploadedPhotos: true,
      });
    }
    this.toggleReasonInput = this.toggleReasonInput.bind(this)
  }

  componentWillMount() {
    const user$ = appState$.pipe(
      map(state => state.user.currentUser)
    );

    this.subscriptions.push(
      user$.subscribe(currentUser => this.setState({ currentUser }))
    );
  }

  componentDidUpdate(p, s) {
    // This method needs to be called on updates.
    // If called at any moment, it does not return
    // the updated form data
    this.props.setOnSave(this.onSave);
  }

  setReasonMessage(e) {
    this.setState({
      reason: e.target.value
    })
  }

  toggleReasonInput() {
    setTimeout(() => {
      this.reasonRef.current.focus();
    }, 200);

    this.setState({
      reasonInputVisible: true
    })
  }

  togglePreSale = () => {
    const fields = this.state.form.fields;
    
    if (fields['preSale'].value == 1) {
      hopplerUpdateFormFieldValue(this.form$, this.state.form, 'preSale', 0)
    } else {
      hopplerUpdateFormFieldValue(this.form$, this.state.form, 'preSale', 1)
    }

  }

  setupForm(props: ListingEditorProps) {
    let fields: FieldList = constructFields(props);

    this.form$ = new Subject<Form>();
    const form = new Form(fields);
    Validators.validateForm(form);

    this.setState({ form });

    this.subscriptions.push(
      this.form$.pipe(distinctUntilChanged())
        .subscribe(form => {
          Validators.validateForm(form);

          let state = {};
          state['form'] = form;
          this.setState(state);
        })
    );

    this.subscriptions.push(
      this.form$.pipe(
        map(form => form.fields['type']),
        distinctUntilChanged()
      ).subscribe((type) => {
        let form = this.state.form;

        const currentDivCat: DivCat = {
          division: form.fields['division'].value,
          category: form.fields['category'].value,
          type: form.fields['type'].value
        };

        let locationLabel = 'Area name';
        let addreNoLabel = 'Lot number';

        const requiresBuilding = REQUIRES_BUILDING.find(member => {
          return member.label === form.fields['type'].value;
        });

        if (requiresBuilding) {
          locationLabel = 'Building name';
          addreNoLabel = 'Unit number';
        } else if (form.fields['type'].value === 'House and Lot' ||
          form.fields['type'].value === 'Townhouse') {
          addreNoLabel = 'House number';
        } else if (form.fields['type'].value === 'Warehouse') {
          addreNoLabel = 'Unit number';
        } else if (form.fields['type'].value === 'Resort' ||
          form.fields['type'].value === 'Building') {
          locationLabel = 'Tower name/number';
          addreNoLabel = 'Street number';
        }

        this.setState({
          currentDivCat: currentDivCat,
          addreNoLabel: addreNoLabel,
          searchFieldLabel: locationLabel,
          requiresBuilding: requiresBuilding ? true : false
        });

      })
    );
  }

  onSave = () => {
    return {
      form: this.state.form,
      currentLocation: this.state.currentLocation,
      locationForm: this.state.locationForm,
      multimedia: this.state.multimedia
    };
  }

  doClose = () => {
    this.props.close();
  }

  getSpinnerBlock() {
    return (
      <div className="mx-3">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  onUploadPhotos = async () => {
    const photos = this.state.photosForUpload;

    this.setState({
      photoUploadState: REQUEST_IN_PROGRESS
    });

    let uploadedPhotos: KumitaMultimedia[];

    try {
      uploadedPhotos = await photoUploadService.upload(photos);
      if (!this.state.multimedia || this.state.multimedia.length < 1) {
        uploadedPhotos[0].isPrimary = 1;
      } else {
        uploadedPhotos = [ ...this.state.multimedia, ...uploadedPhotos ];
      }

      this.setState({
        hasUploadedPhotos: true,
        multimedia: uploadedPhotos,
        photoUploadState: REQUEST_SUCCESS,
        isReviewingPhotos: false,
        photosForUpload: []
      });
    } catch (err) {
      console.error(err);
      this.setState({
        photoUploadState: REQUEST_FAILED,
        isReviewingPhotos: false
      });
    }
  }

  onCancelPhotoUpload = () => {
    this.setState({
      isReviewingPhotos: false,
      isEditingPhotos: false,
      photosForUpload: [],

      // Reset any changes made by PostPropertyPhoto
      multimedia: [ ...this.props.listing.multimedia ]
    });
  }

  onPhotoReviewStateChange(inReview: boolean) {
    this.setState({
      isReviewingPhotos: inReview,
      photoUploadState: REQUEST_UNTOUCHED
    });
  }

  onLocationChange = (location: Location) => {
    this.setState({
      currentLocation: location
    });
  }

  onUpdateFormFields = (form: Form) => {
    // console.log('onUpdateFormFields', form);
    this.setState({
      locationForm: form
    });
  }

  onParkingSpacesChange = (value) => {
    let form = this.state.form;

    hopplerUpdateFormFieldValue(this.form$, form, 'parkingSpaces', value);
  }

  onEditPhotoToggle = () => {
    this.setState({
      isEditingPhotos: !this.state.isEditingPhotos
    })
  }

  deleteProperty(propertyKey: string) {

    const store = getStore();

    const listings$ = appState$.pipe(
      map(app => app.listing)
    );

    listings$
      .pipe(map(listing => listing))
      .subscribe((listing) => {
        this.setState({
          deletePropertyStatus: listing.deletePropertyStatus
        })
    })

    store.dispatch({
      type: DELETE_LISING,
      payload: {
        focusedPropertyKey: propertyKey,
        reason: this.state.reason
      }
    });   
  }
  
  // ~COMPONENTS AREA~
  getPhotoEditBlock(props: ListingEditorProps, state: ListingEditorState) {
    if (state.isEditingPhotos) {
      return (
        <div className="mt-3">
          <PostPropertyPhoto 
            onReviewStateChange={ inReview => this.onPhotoReviewStateChange(inReview) }

            photosForUpload={ state.photosForUpload }
            onPhotoSelectionChange={ photosForUpload => this.setState({ photosForUpload }) }

            hasUploaded={ state.hasUploadedPhotos }
            uploadedPhotos={ state.multimedia }
            onUploadedPhotosChange={ multimedia => this.setState({ multimedia }) }
            photoUploadState={ state.photoUploadState } />
        </div>
      );
    }
  }

  canUploadPhotos(state: ListingEditorState) {
    return (
      state.photoUploadState !== REQUEST_IN_PROGRESS &&
      state.photosForUpload &&
      state.photosForUpload.length >= 1
    );
  }

  getPhotoEditActions(props: ListingEditorProps, state: ListingEditorState) {
    if (this.state.isEditingPhotos) {
      return (
        <div className="d-flex mt-3 hi-postprop__actions__button-container">
          { state.photoUploadState == REQUEST_IN_PROGRESS ? this.getSpinnerBlock() : null }
          
          <button 
            type="button"
            className="btn btn-outline-dark w-50" 
            disabled={state.photoUploadState === REQUEST_IN_PROGRESS}
            onClick={ this.onCancelPhotoUpload }>
            Cancel
          </button>

          <button 
            className="btn btn-secondary ml-1 w-50"
            type="button"
            disabled={ !this.canUploadPhotos(state) }
            onClick={ this.onUploadPhotos }>
            Upload
          </button>
        </div>
      );
    }
  }

  getPropertyTypeBlock(props: ListingEditorProps, state: ListingEditorState) {
    let form = state.form;

    if (form.fields['division'].value == 'Residential') {
      return (
        <div className="row">
          {
            residentialPropTypes.map((option) => 
            <span className="hi-input-group__checkbox col-12 col-md-3 mr-0">
              <label>
                <input
                  disabled={ state.isPending == 1 || !props.listing.canEdit }  
                  type="radio" name="type" 
                  onChange={ hopplerLinkState(this.form$, form, 'type') }
                  value={option.label} 
                  checked={form.fields['type'].value == option.label}
                  />
                { option.label }
              </label>
            </span>
            )
          }
        </div>
      );
    } else {
      let commercialTypesToUse;

      if (form.fields['category'].value == 'Commercial') {
        commercialTypesToUse = commercialPropTypes.filter(type => {
          if (type.label != 'Industrial Lot' && type.label != 'Resort') {
            return type;
          }
        });
      } else {
        commercialTypesToUse = commercialPropTypes.filter(type => {
          if (type.label != 'Serviced Office') {
            return type;
          }
        });
      }

      return (
        <div className="row">
          {
            commercialTypesToUse.map((option) => 
            <span className="hi-input-group__checkbox col-3 mr-0">
              <label>
                <input
                  disabled={ state.isPending == 1 || !props.listing.canEdit }
                  type="radio" name="type" 
                  onChange={ hopplerLinkState(this.form$, form, 'type') }
                  value={option.label} 
                  checked={form.fields['type'].value == option.label}
                  />
                { option.label }
              </label>
            </span>
            )
          }
        </div>
      );
    }
  }

  getLocationBlock(props: ListingEditorProps, state: ListingEditorState) {

    return (
      <div className="mt-4">
        <PostPropertyLocation
          addreNoLabel={state.addreNoLabel}
          searchFieldLabel={state.searchFieldLabel}
          requiresBuilding={state.requiresBuilding}
          currentDivCat={state.currentDivCat}
          onLocationChange={this.onLocationChange}
          currentLocation={state.currentLocation}
          onUpdateFormFields={this.onUpdateFormFields}
          onDupeCheck={() => {}}
          canEdit={ state.isPending == 1 || props.listing.canEdit != 0 }
          listing={ props.listing } />
      </div>
    );
  }

  getPrivateNotesBlock(props: ListingEditorProps, state: ListingEditorState) {
    let form = state.form;

    return (
      <div className="row mt-4 mb-4">
        <div className="col-12">
          <label className="hi-input-group__label">Private Notes</label>
          <textarea 
            disabled={ state.isPending == 1 || !props.listing.canEdit }
            placeholder="Write your message here"
            className="form-control hi-listing-editor-modal__editor__notes"
            value={ form.fields['notes'].value }
            onInput={ hopplerLinkState(this.form$, form, 'notes') } >
          </textarea>
        </div>
      </div>
    );
  }

  getListingSourceBlock(props: ListingEditorProps, state: ListingEditorState) {
    
    const form = state.form;
    const sources = ['Direct', 'Network'];

    return (
      <div className="row mt-4 mb-4">
        <div className="col-12">
          <label className="hi-input-group__label">Source</label>
          <select
            disabled={ state.isPending == 1 || !props.listing.canEdit }
            className="form-control"
            onChange={ hopplerLinkState(this.form$, form, 'source') }
            value={ form.fields['source'].value } 
            name="source">
            <option value="" disabled selected>Listing source</option>
            {
              sources.map((src, i) => <option
                key={ i }
                value={ src }>{ src }</option> )
            }
          </select>
        </div>
      </div>
    );
  }

  getErrorBlock(state: ListingEditorState, fieldName: string, message: string) {
    if (Validators.cxValidateField(state.form.fields[fieldName])) {
      return (
        <div className="hi-error-help-text">
          <i className="fas fa-exclamation-circle"></i> { message }
        </div>
      );
    }
  }

  getReasonMessageBlock(props: ListingEditorProps, state?: ListingEditorState) {

    const { declineReason } = props.listing;
    const defaultMessage: string = "No comments found."

    const declineMsg = declineReason == undefined ? defaultMessage : declineReason;
    const currentStage = this.props.currentTab;

    if (props.reasonVisibility){
      switch(currentStage) {
        case 'pending':
          return <p className="p-2 rounded declined-message mt-3 mb-1">{ declineMsg }</p>

        case 'declined':
          return <p className="p-2 rounded declined-message mt-3 mb-1">{ declineMsg }</p>

        default:
          return <p className="p-2 rounded declined-message mt-3 mb-1"><i className="text-default">No message found.</i></p>
      }
    }
  }

  getDescriptionBlock(props: ListingEditorProps, state?: ListingEditorState) {
    return (
      <div>
         <div className="hi-input-group mt-3">
            <label className="hi-input-group__label">Description*</label>
            <textarea 
              disabled={ state.isPending == 1 }
              placeholder="Write your message here"
              className="form-control hi-listing-editor-modal__editor__notes"
              value={ state.form.fields['description'].value }
              onInput={ hopplerLinkState(this.form$, state.form, 'description') } >
            </textarea>
          </div>
      </div>
    )
  }

  getArchiveBlock(props: ListingEditorProps, state: ListingEditorState) {
    const isRequesting: boolean = state.deletePropertyStatus == REQUEST_IN_PROGRESS

    if (!props.listing.canEdit) {
      return null;
    }
    
    return (
      <div className="my-4">
      <hr/>

      <p className="hi-input-group__label">LAST MODIFIED BY</p>

      <div className="mt-3">
        <p className="mb-0 font-weight-bold">{ props.listing['modifiedByName'] }</p>
        <p className="mb-0 text-muted">{ props.listing['modifiedByMobile'] }</p>
        <p className="mb-0 text-muted">{ props.listing['modifiedByEmail'] }</p>
      </div>
      
      <button 
        type="button"
        onClick={ this.toggleReasonInput }
        className={ cx('px-4 btn btn-md btn-outline-dark archive-button mt-5', {
          'd-none': state.reasonInputVisible
        }) }>
        Archive
      </button>

      {
        state.reasonInputVisible && (
          <div className="mt-5">
            <div className="form-row">
            <div className={ cx('form-group', {
              'col-11': isRequesting,
              'col-12': !isRequesting
            }) }>
              <label className="hi-input-group__label">Archive Reason</label>
              <input
                disabled={ isRequesting }
                ref={ this.reasonRef }
                placeholder="Archive Reason"
                onChange={e => this.setReasonMessage(e)} 
                type="text" 
                className="form-control"/>
              </div>
                {
                  state.deletePropertyStatus == REQUEST_IN_PROGRESS &&
                  <div className="d-flex align-items-center justify-content-center col-1">
                    { this.getSpinnerBlock() }
                  </div>
                }
            </div>  
            {
              !isRequesting &&
              <div className="button-group w-100 text-right">
                <button
                  type="button"
                  onClick={e => this.setState({ reasonInputVisible: false })} 
                  className="btn btn-default border-right">
                  <span className="text-dark">Cancel</span>
                </button>

                <button
                  onClick={ e => this.deleteProperty(props.listing.propertyKey) }
                  type="button" 
                  className="btn btn-default">
                  <span className="text-dark">Delete</span>
                </button>
              </div>
            }
          </div>
        )
      }
      </div>
    )
  }

  getPresaleBlock(props: ListingEditorProps, state: ListingEditorState) {

    const { category, division } = props.listing;
    const { form } = state;

    if (category == 'Sale' && division == 'Residential') {
      return (
        <div className="form-group">
          <label className="hi-input-group__label">Pre-selling</label>
          <div className="form-check">
            <input 
              type="checkbox"
              name="preSale"
              id="preSale"
              onChange={ this.togglePreSale }
              checked={ form.fields['preSale'].value }
              className="form-check-input" />
            <label
              className="form-check-label" 
              for="preSale">Pre-selling</label>
          </div>
        </div>
      )
    }
  }

  getTurnOverDateBlock(props: ListingEditorProps, state: ListingEditorState) {

    const { form } = state;
    
    if (form.fields['preSale'].value) {
      return (
        <div className="row">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Turnover Date</label>
              <DatePickerControl className='form-control'
                disabled={ state.isPending == 1 || !props.listing.canEdit }
                onChange={ hopplerLinkState(this.form$, form, 'dateTurnover') }
                value={ state.form.fields['dateTurnover'].value } />
            </div>
          </div>
        </div>
      )
    }
  }

  doShowContact(props: ListingEditorProps, state: ListingEditorState) {
    const currentUserRoles = state.currentUser.roles;
    const canEdit = Boolean(props.listing.canEdit);
    const isAssigned = Boolean(props.listing.salesDirectorProfile);
    if (canEdit) {
      return true;
    } else {
      if (currentUserRoles.includes('ROLE_SALES_DIRECTOR') && !isAssigned) {
        // Internal sales director
        return true;
      } else if (currentUserRoles.includes('ROLE_EXTERNAL_SALES_DIRECTOR') && isAssigned) {
        // External sales director
        return false;
      } else if ((currentUserRoles.includes('ROLE_ADMIN') || currentUserRoles.includes('ROLE_SUPERADMIN')) && isAssigned) {
        // Internal manager
        return true;
      }
    }
    return false;
  }

  render(props: ListingEditorProps, state: ListingEditorState) {

    if (!state.form) {
      return;
    }
    const showContact: boolean = this.doShowContact(props, state)

    return (
      <form className="hi-listing-editor-modal__editor mb-3">

        { this.state.isPending ?
          <div className="mt-3">
            <i className="text-dark">*unable to edit listing until approved.</i>
          </div> : null
        }

        <ListingEditorStatusSection 
          form={ this.state.form }
          formSubject={ this.form$ }
          onHoldRef={ this.onHoldRef }
        />

        { this.getReasonMessageBlock(props, state) }
        
        <ListingEditorMultimediaSection 
          listing={ props.listing }
          photoUploadState={ this.state.photoUploadState }
          isPending={ this.state.isPending }
          isEditingPhotos={ this.state.isEditingPhotos }
          onEditPhotoToggle={ this.onEditPhotoToggle }>
          { this.getPhotoEditBlock(props, state) }
          { this.getPhotoEditActions(props, state) }
        </ListingEditorMultimediaSection>

        { this.getLocationBlock(props, state) }

        <ListingEditorEssentialSection 
          form={ this.state.form }
          listing={ props.listing }
          formSubject={ this.form$ } 
          requiresBuilding={ this.state.requiresBuilding }
          isPending={ this.state.isPending }
        />

        { this.getPresaleBlock(props, state) }

        { this.getTurnOverDateBlock(props, state) }

        <ListingEditorAmenitiesSection 
          form={ this.state.form }
          requiresBuilding={ this.state.requiresBuilding }
          listing={ props.listing }
          formSubject={ this.form$ }
          isPending={ this.state.isPending }
        />

        <ListingEditorOtherSection 
          form={ this.state.form }
          isPending={ this.state.isPending }
          listing={ props.listing }
          currentUser={ this.state.currentUser }
          formSubject={ this.form$ }
        />

        <ListingEditorPrimaryContactSection 
          listing={ props.listing }
          currentUser={ this.state.currentUser }
          showContact={ showContact }
        />

        { showContact &&
        <ListingEditorAlternativeContactSection 
          listing={ props.listing }
          form={ this.state.form }
          isPending={ this.state.isPending }
          formSubject={ this.form$ }
          currentUser={ this.state.currentUser }
        />
        }

        { this.getListingSourceBlock(props, state) }

        { this.getPrivateNotesBlock(props, state) }

        { this.getArchiveBlock(props, state) }

      </form>
    );
  }

  componentWillUnmount() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }
}